import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, MapPin, Calendar, Clock, ArrowRight, Star, Shield, Award } from 'lucide-react';
import LocationInput from './LocationInput';
import { useModalStore } from '../store/modalStore';

interface HeroSliderProps {
  images?: string[];
}

const HeroSlider: React.FC<HeroSliderProps> = () => {
  const { openBookingModal } = useModalStore();
  const [formData, setFormData] = useState({
    pickup: '',
    dropoff: '',
    date: '',
    time: '',
    pickupPlaceId: '',
    dropoffPlaceId: ''
  });

  const handleQuickBook = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.pickup || !formData.dropoff) {
      alert('⚠️ Veuillez renseigner les adresses de départ et d\'arrivée');
      return;
    }
    
    if (!formData.date || !formData.time) {
      alert('⚠️ Veuillez sélectionner une date et une heure');
      return;
    }
    
    openBookingModal(formData);
  };

  const today = new Date().toISOString().split('T')[0];
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Image de fond Mercedes de luxe */}
      <div className="absolute inset-0 z-0">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, ease: "easeOut" }}
          className="h-full w-full"
        >
          <img
            src="https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?q=80&w=2070&auto=format&fit=crop"
            alt="Mercedes de luxe avec chauffeur"
            className="h-full w-full object-cover opacity-50"
            loading="eager"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-black/80" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-900/20 via-transparent to-transparent" />
      </div>

      {/* Grille futuriste */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />

      {/* Particules flottantes */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute h-1 w-1 rounded-full bg-yellow-500/30"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{
              y: [null, Math.random() * window.innerHeight],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>

      {/* Contenu principal */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Section gauche - Texte */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -30 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col justify-center"
          >
            {/* Badge Premium */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-4 py-2 backdrop-blur-sm"
            >
              <Sparkles className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-semibold text-yellow-500">Service VTC Premium</span>
            </motion.div>

            {/* Titre principal */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-6 text-5xl font-bold leading-tight text-white lg:text-6xl xl:text-7xl"
            >
              L'Excellence du
              <span className="block bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                Transport de Luxe
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-8 text-lg leading-relaxed text-gray-300 lg:text-xl"
            >
              Voyagez dans le confort absolu avec nos véhicules de prestige et nos chauffeurs expérimentés.
              Réservez votre transfert en quelques clics.
            </motion.p>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="grid grid-cols-3 gap-6"
            >
              <div className="text-center">
                <div className="mb-2 flex items-center justify-center">
                  <Star className="h-6 w-6 text-yellow-500" />
                </div>
                <div className="text-2xl font-bold text-white">4.9/5</div>
                <div className="text-sm text-gray-400">Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="mb-2 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-yellow-500" />
                </div>
                <div className="text-2xl font-bold text-white">24/7</div>
                <div className="text-sm text-gray-400">Disponible</div>
              </div>
              <div className="text-center">
                <div className="mb-2 flex items-center justify-center">
                  <Award className="h-6 w-6 text-yellow-500" />
                </div>
                <div className="text-2xl font-bold text-white">+10K</div>
                <div className="text-sm text-gray-400">Clients</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Section droite - Formulaire */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center justify-center"
          >
            <div className="w-full max-w-xl">
              {/* Carte du formulaire */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-black/50 via-black/40 to-black/50 p-8 shadow-2xl backdrop-blur-xl"
              >
                {/* Effet de lueur */}
                <div className="absolute -top-24 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-yellow-500/20 blur-3xl" />
                
                {/* Titre du formulaire */}
                <div className="relative mb-8 text-center">
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="absolute inset-0 rounded-2xl bg-gradient-to-r from-yellow-500/20 via-yellow-600/20 to-yellow-500/20 blur-xl"
                  />
                  <h2 className="relative text-3xl font-bold text-white">
                    Réservez votre course
                  </h2>
                  <p className="mt-2 text-sm text-gray-400">Remplissez le formulaire ci-dessous</p>
                </div>

                {/* Formulaire */}
                <form onSubmit={handleQuickBook} className="relative space-y-5">
                  {/* Adresse de départ */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <LocationInput
                      label="Point de départ"
                      placeholder="Entrez votre adresse de départ"
                      value={formData.pickup}
                      onLocationSelect={(location) => {
                        setFormData(prev => ({ 
                          ...prev, 
                          pickup: location.address,
                          pickupPlaceId: location.placeId || ''
                        }));
                      }}
                      icon={<MapPin className="h-5 w-5 text-green-500" />}
                    />
                  </motion.div>

                  {/* Adresse d'arrivée */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <LocationInput
                      label="Destination"
                      placeholder="Entrez votre destination"
                      value={formData.dropoff}
                      onLocationSelect={(location) => {
                        setFormData(prev => ({ 
                          ...prev, 
                          dropoff: location.address,
                          dropoffPlaceId: location.placeId || ''
                        }));
                      }}
                      icon={<MapPin className="h-5 w-5 text-red-500" />}
                    />
                  </motion.div>

                  {/* Date et heure */}
                  <div className="grid grid-cols-2 gap-4">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                    >
                      <label className="mb-2 block text-sm font-medium text-gray-300">
                        Date
                      </label>
                      <div className="group relative">
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                          min={today}
                          required
                          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-gray-500 backdrop-blur-sm transition-all focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/20"
                        />
                        <Calendar className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-yellow-500 transition-transform group-focus-within:scale-110" />
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 }}
                    >
                      <label className="mb-2 block text-sm font-medium text-gray-300">
                        Heure
                      </label>
                      <div className="group relative">
                        <input
                          type="time"
                          value={formData.time}
                          onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                          required
                          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-gray-500 backdrop-blur-sm transition-all focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/20"
                        />
                        <Clock className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-yellow-500 transition-transform group-focus-within:scale-110" />
                      </div>
                    </motion.div>
                  </div>

                  {/* Bouton de soumission */}
                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="group relative mt-6 w-full overflow-hidden rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 px-8 py-4 font-semibold text-black shadow-lg shadow-yellow-500/30 transition-all hover:shadow-xl hover:shadow-yellow-500/40"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-500"
                      initial={{ x: '-100%' }}
                      whileHover={{ x: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                    <span className="relative flex items-center justify-center gap-2">
                      Réserver maintenant
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </motion.button>

                  {/* Info */}
                  <p className="text-center text-xs text-gray-500">
                    🔒 Réservation sécurisée • Annulation gratuite
                  </p>
                </form>
              </motion.div>

              {/* Trust badges */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-6 flex items-center justify-center gap-6 text-gray-400"
              >
                <div className="flex items-center gap-2 text-sm">
                  <Shield className="h-4 w-4 text-yellow-500" />
                  <span>Paiement sécurisé</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span>5★ Service</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Effet de lueur en bas */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent" />
    </div>
  );
};

export default HeroSlider;
